import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import { ApiConfig } from '../../../application/configurations/IConfig';
import { SearchModel } from '../../../application/model/analytics/searchModel';
import { RecentDeleteModel } from '../../../application/model/analytics/recentDeleteModel';
import { appConstants } from '../../../application/configurations/constants';
import { ResultClickModel } from '../../../application/model/analytics/resultClickModel';
import { AnalyticsConstants } from '../../../application/model/analytics/constants';
import { PageLoadModel } from '../../../application/model/analytics/pageLoadModel';
import { RefinerClickModel } from '../../../application/model/analytics/refinerClickModel';
import { DateFilterConfig } from '../../../presentation/components/dateFilter/config';
import { sha256 } from 'js-sha256';
import { UtilityService } from '../utils';
import { ItemClickTypeAnalytics } from '../../../application/model/analytics/constants';
/**
 * class for processing async functions based on actions
 */
class AnalyticsEvents {
  private analyticsApiConfig: ApiConfig;

  constructor() {
    this.analyticsApiConfig = { ...apiConfig.analytics };
  }

  /**
   * async method to trigger analytics search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   * @param  {number} resultCount search result count
   */
  pageLoad = async (loadTime: number, accounts: Array<Object>) => {
    if (loadTime) {
      let loadpagePayload;
      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new PageLoadModel({
        ...analyticsConfig.data,
        LoadTime: loadTime
      });
      loadpagePayload = analyticsPayload.analyticsData.loadPage
        ? analyticsPayload.analyticsData.loadPage[0]
        : [];
      analyticsConfig.data = loadpagePayload;
      analyticsConfig.accountInfo = accounts;
      ApiService.requestApi([analyticsConfig]);
    }
  };

  /**
   * async method to trigger analytics search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   * @param  {number} resultCount search result count
   * @param {Array<Object>} accounts accounts
   * @param {boolean} browsePageSearch browse page search flag
   * @param {string} vertical search vertical
   */
  searchAction = async (
    searchTerm: string,
    accounts: Array<Object>,
    browsePageSearch: boolean,
    vertical: string,
    resultCount: number = 0
  ) => {
    let searchPayload;
    if (searchTerm) {
      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new SearchModel({
        ...analyticsConfig.data,
        Query: searchTerm,
        ActionValue: searchTerm,
        Results: resultCount,
        PageURL: browsePageSearch
          ? appConstants.routePaths.browse
          : appConstants.routePaths.results,
        Vertical: vertical
      });
      searchPayload = analyticsPayload.analyticsData.query
        ? analyticsPayload.analyticsData.query[0]
        : [];
      analyticsConfig.data = searchPayload;
      analyticsConfig.accountInfo = accounts;
      await ApiService.requestApi([analyticsConfig]);
    }
  };

  /**
   * async method to trigger analytics Recent search and Suggestins click action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param   {string} searchTerm  search query term
   * @param  {number} resultCount search result count
   * @param {Array<Object>} accounts accounts
   * @param {boolean} browsePageSearch browse page search flag
   * @param {string} vertical search vertical
   */
  recentSearchAndSuggestionsClick = async (
    searchTerm: string,
    accounts: Array<Object>,
    browsePageSearch: boolean,
    WasRecentSearch: string,
    resultCount: number = 0
  ) => {
    let recentSearchAndSuggestionsPayload: any;
    if (searchTerm) {
      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new SearchModel({
        ...analyticsConfig.data,
        Query: searchTerm,
        ActionValue: searchTerm,
        WasRecentSearch: WasRecentSearch,
        Results: resultCount,
        PageURL: browsePageSearch
          ? appConstants.routePaths.browse
          : appConstants.routePaths.results,
        WasSuggested: AnalyticsConstants.trueString
      });
      recentSearchAndSuggestionsPayload = analyticsPayload.analyticsData.query
        ? analyticsPayload.analyticsData.query[0]
        : [];
      analyticsConfig.data = recentSearchAndSuggestionsPayload;
      analyticsConfig.accountInfo = accounts;
      await ApiService.requestApi([analyticsConfig]);
    }
  };

  /**
   * async method to trigger analytics Recent item click action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param   {string} Title  item title
   * @param  {number} DetailPath item detail path
   * @param {Array<Object>} accounts accounts
   * @param {boolean} browsePageSearch browse page search flag
   * @param {string} index item posItion
   */

  recentSearchItemClick = async (
    Title: string,
    DetailPath: string,
    Vertical: string,
    accounts: Array<Object>,
    browsePageSearch: boolean,
    index: any
  ) => {
    let recentSearchItemClickPayload: any;
    if (Title) {
      let hashedDetailPath: string = DetailPath;
      if (Vertical === appConstants.verticals.People) {
        let decodedPeopleDetailPath = decodeURIComponent(DetailPath);
        let peopleMailId = decodedPeopleDetailPath
          ? decodedPeopleDetailPath.split('|')[decodedPeopleDetailPath.split('|').length - 1]
          : '';
        let hashedPeopleMailId = peopleMailId
          ? sha256(peopleMailId.toLowerCase())
          : '';
        Title = Title ? sha256(Title.toLowerCase()) : '';
        hashedDetailPath =
          Vertical === appConstants.verticals.People
            ? decodedPeopleDetailPath.replace('i:0#', 'i%3A0%23').replace(peopleMailId, hashedPeopleMailId)
            : decodedPeopleDetailPath;
      }

      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new ResultClickModel({
        ...analyticsConfig.data,
        Title: Title,
        Document: hashedDetailPath,
        ActionValue: hashedDetailPath,
        ActionsSequence: {
          1: AnalyticsConstants.Query,
          2: AnalyticsConstants.OpenResultItem + hashedDetailPath
        },
        PageURL: browsePageSearch
          ? appConstants.routePaths.browse
          : appConstants.routePaths.results,
        WasRecentSearch: AnalyticsConstants.trueString,
        WasSuggested: AnalyticsConstants.trueString,
        IsDocument: true,
        Position: index
      });
      recentSearchItemClickPayload = analyticsPayload.analyticsData.document
        ? analyticsPayload.analyticsData.document[0]
        : null;
      analyticsConfig.data = recentSearchItemClickPayload;
      analyticsConfig.accountInfo = accounts;
      await ApiService.requestApi([analyticsConfig]);
    }
  };

  /**
   * async method to trigger analytics Recent search and recent clicked action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param {boolean} browsePageSearch browse page search flag
   * @param {Array<Object>} accounts accounts
   */
  recentSearchRemoval = async (accounts: Array<Object>): Promise<void> => {
    let recentDeletePayload: any;
    const analyticsConfig = { ...this.analyticsApiConfig };
    const analyticsPayload = new RecentDeleteModel({
      ...analyticsConfig.data
    });
    recentDeletePayload = analyticsPayload.analyticsData.recentDelete
      ? analyticsPayload.analyticsData.recentDelete[0]
      : null;
    analyticsConfig.data = recentDeletePayload;
    analyticsConfig.accountInfo = accounts;
    await ApiService.requestApi([analyticsConfig]);
  };

  /**
   * async method to trigger analytics result click action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   * @param  {number} resultCount search result count
   * @param {Array<Object>} accounts accounts
   * @param {boolean} browsePageSearch browse page search flag
   * @param {string} vertical search vertical
   * @param {any} resultitemobj search item clicked data object
   * @param {number} pageNumber search item clicked origin page number
   * @param {number} index search item clicked position
   */
  resultClickAction = async (
    searchTerm: string,
    accounts: Array<Object>,
    browsePageSearch: boolean,
    vertical: string,
    sourceType: any,
    resultitemobj: any,
    pageNumber: number,
    index: number,
    resultCount: number = 0
  ) => {
    let resultClickActionPayload: any;
    let resultsPerPage: any = localStorage.getItem(
      appConstants.noOfItemsPerPage
    )
      ? localStorage.getItem(appConstants.noOfItemsPerPage)
      : appConstants.defaultItemsPerPage;
    if (searchTerm) {
      let hashedDetailPath: string = resultitemobj.detailPath;
      let hashedTitle: string = resultitemobj.title;
      let clickType: string = sourceType ? sourceType : ItemClickTypeAnalytics.Default;
      if (resultitemobj.vertical === appConstants.verticals.People) {
        let decodedPeopleDetailPath = decodeURIComponent(resultitemobj.detailPath);
        let peopleMailId = decodedPeopleDetailPath 
          ? decodedPeopleDetailPath.split('|')[
              decodedPeopleDetailPath.split('|').length - 1
            ]
          : '';
        let hashedPeopleMailId = peopleMailId
          ? sha256(peopleMailId.toLowerCase())
          : '';
        hashedTitle = resultitemobj.name
          ? sha256(
              UtilityService.removeHtmlTags(resultitemobj.name).toLowerCase()
            )
          : hashedTitle;
        hashedDetailPath =
          resultitemobj.vertical === appConstants.verticals.People
            ? decodedPeopleDetailPath.replace('i:0#', 'i%3A0%23').replace(peopleMailId, hashedPeopleMailId)
            : decodedPeopleDetailPath;
      }
      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new ResultClickModel({
        ...analyticsConfig.data,
        Query: searchTerm,
        ActionValue: hashedDetailPath,
        Results: resultCount,
        PageURL: browsePageSearch
          ? appConstants.routePaths.browse
          : appConstants.routePaths.results,
        Vertical: vertical,
        Title: hashedTitle,
        Document: hashedDetailPath,
        ActionsSequence: {
          1: AnalyticsConstants.Query + searchTerm,
          2: AnalyticsConstants.OpenResultItem + hashedDetailPath
        },
        QueriesLeadingToDocument: [searchTerm],
        Page: pageNumber.toString(),
        Position: index + 1 + (pageNumber - 1) * resultsPerPage,
        ItemClickType: clickType
      });
      resultClickActionPayload = analyticsPayload.analyticsData.document
        ? analyticsPayload.analyticsData.document[0]
        : null;
      analyticsConfig.data = resultClickActionPayload;
      analyticsConfig.accountInfo = accounts;
      await ApiService.requestApi([analyticsConfig]);
    }
  };

  /**
   * async method to trigger analytics search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   * @param  {number} resultCount search result count
   * @param {Array<Object>} accounts accounts
   * @param {boolean} browsePageSearch browse page search flag
   * @param {number} filterByDate Filter applied in search
   */
  refinerAction = async (
    searchTerm: string,
    accounts: Array<Object>,
    browsePageSearch: boolean,
    filterByDate: number = 12,
    refinersClicked? : Array<Object>,
    flagForContentAge? : boolean,
    flagForClear?: boolean,
    resultCount: number = 0,
    vertical?: string | null
  ) => {
    let refinerPayload: any;
    if (searchTerm) {
      const filterOption = DateFilterConfig.filterOptions.filter(
        (option) => option.value === filterByDate
      );
      const analyticsConfig = { ...this.analyticsApiConfig };
      const analyticsPayload = new RefinerClickModel({
        ...analyticsConfig.data,
        Query: searchTerm,
        Results: resultCount,
        PageURL: browsePageSearch
          ? appConstants.routePaths.browse
          : appConstants.routePaths.results,
          RefinerValues: flagForContentAge ? refinersClicked : filterOption[0] ? [filterOption[0].name] : '',
          FlagForContentAge: flagForContentAge,
          Vertical: vertical || null
      });
      refinerPayload = analyticsPayload.analyticsData.refiner
        ? analyticsPayload.analyticsData.refiner[0]
        : null;
      analyticsConfig.data = refinerPayload;
      analyticsConfig.accountInfo = accounts;
      analyticsConfig.refinersClicked = refinersClicked;
      if(flagForClear!==true)
      await ApiService.requestApi([analyticsConfig]);
    }
  };
}

const AnalyticsUseCase = new AnalyticsEvents();
export default AnalyticsUseCase;